import { io } from 'socket.io-client';
import { APP_DOMAIN_PREFIX_PAYMENTS } from '../environment';

// Socket Service Module Loaded

class SocketService {
  constructor() {
    console.log('🏗️  SocketService Constructor Called');
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
  }

  /**
   * Initialize and connect to the WebSocket server
   * @param {string} serverUrl - The WebSocket server URL (optional, defaults to PAYMENT service)
   * @param {object} options - Socket.io connection options
   */
  connect(serverUrl = null, options = {}) {
    if (this.socket && this.isConnected) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🔌 Socket already connected');
      console.log('   Socket ID:', this.socket.id);
      console.log('   Connected:', this.socket.connected);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return this.socket;
    }

    const url = serverUrl || APP_DOMAIN_PREFIX_PAYMENTS;
    
    // Default options for socket connection
    const defaultOptions = {
      transports: ['websocket', 'polling'], // Try WebSocket first, fallback to polling
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 20000,
      autoConnect: true,
      ...options
    };

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔌 INITIATING WEBSOCKET CONNECTION');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🌐 Server URL:', url);
    console.log('⚙️  Options:', JSON.stringify(defaultOptions, null, 2));
    console.log('⏰ Timestamp:', new Date().toLocaleString());
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    
    this.socket = io(url, defaultOptions);

    // Connection event handlers
    this.socket.on('connect', () => {
      this.isConnected = true;
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('✅ WEBSOCKET CONNECTED SUCCESSFULLY');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🆔 Socket ID:', this.socket.id);
      console.log('🔗 Connected to:', url);
      console.log('🚀 Transport:', this.socket.io.engine.transport.name);
      console.log('⏰ Connected at:', new Date().toLocaleString());
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('❌ WEBSOCKET DISCONNECTED');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📛 Reason:', reason);
      console.log('⏰ Disconnected at:', new Date().toLocaleString());
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    });

    this.socket.on('connect_error', (error) => {
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('❌ WEBSOCKET CONNECTION ERROR');
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('🌐 Target URL:', url);
      console.error('📛 Error:', error.message);
      console.error('🔍 Error Type:', error.type);
      console.error('📝 Full Error:', error);
      console.error('⏰ Error at:', new Date().toLocaleString());
      console.error('💡 Troubleshooting Tips:');
      console.error('   1. Check if backend server is running');
      console.error('   2. Verify CORS settings on backend');
      console.error('   3. Confirm WebSocket endpoint is correct');
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🔄 WEBSOCKET RECONNECTED');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🔢 After attempts:', attemptNumber);
      console.log('🆔 New Socket ID:', this.socket.id);
      console.log('⏰ Reconnected at:', new Date().toLocaleString());
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      this.isConnected = true;
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 WebSocket reconnection attempt:', attemptNumber, 'at', new Date().toLocaleString());
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('❌ WebSocket reconnection error:', error.message, 'at', new Date().toLocaleString());
    });

    this.socket.on('reconnect_failed', () => {
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('❌ WEBSOCKET RECONNECTION FAILED');
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('📛 All reconnection attempts exhausted');
      console.error('⏰ Failed at:', new Date().toLocaleString());
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    });

    // Catch-all event listener to debug what events are actually being received
    this.socket.onAny((eventName, ...args) => {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🎯 ANY EVENT RECEIVED (onAny listener)');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📌 Event Name:', eventName);
      console.log('📦 Arguments:', args);
      console.log('⏰ Timestamp:', new Date().toLocaleString());
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    });

    return this.socket;
  }

  /**
   * Join a specific room (useful for order-specific or session-specific updates)
   * @param {string} roomId - The room identifier (e.g., orderId, sessionId)
   */
  joinRoom(roomId) {
    if (this.socket && this.isConnected) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🚪 JOINING ROOM');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🏠 Room ID:', roomId);
      console.log('🆔 Socket ID:', this.socket.id);
      console.log('🔗 Connected:', this.socket.connected);
      console.log('⏰ Timestamp:', new Date().toLocaleString());
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      this.socket.emit('join_room', roomId);
      console.log('✅ Join room event emitted');
    } else {
      console.warn('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.warn('⚠️  CANNOT JOIN ROOM - SOCKET NOT CONNECTED');
      console.warn('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.warn('🏠 Attempted Room ID:', roomId);
      console.warn('🔌 Socket exists:', !!this.socket);
      console.warn('✓  Is connected:', this.isConnected);
      console.warn('⏰ Timestamp:', new Date().toLocaleString());
      console.warn('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }
  }

  /**
   * Leave a specific room
   * @param {string} roomId - The room identifier to leave
   */
  leaveRoom(roomId) {
    if (this.socket && this.isConnected) {
      console.log('🚪 Leaving room:', roomId);
      this.socket.emit('leave_room', roomId);
    }
  }

  /**
   * Subscribe to an event
   * @param {string} eventName - The event name to listen to
   * @param {function} callback - The callback function to execute when event is received
   */
  on(eventName, callback) {
    if (this.socket) {
      console.log('📡 Subscribing to event:', eventName);
      
      // Wrap the callback to add logging
      const wrappedCallback = (data) => {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('📨 EVENT RECEIVED FROM SERVER');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('Event Name:', eventName);
        console.log('Timestamp:', new Date().toLocaleTimeString());
        console.log('Data Received:', JSON.stringify(data, null, 2));
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        
        // Call the original callback
        callback(data);
      };
      
      this.socket.on(eventName, wrappedCallback);
      
      // Store the original callback for cleanup later
      if (!this.listeners.has(eventName)) {
        this.listeners.set(eventName, []);
      }
      this.listeners.get(eventName).push({ original: callback, wrapped: wrappedCallback });
    } else {
      console.warn('⚠️ Cannot subscribe to event - socket not initialized');
    }
  }

  /**
   * Unsubscribe from an event
   * @param {string} eventName - The event name to unsubscribe from
   * @param {function} callback - The specific callback to remove (optional)
   */
  off(eventName, callback = null) {
    if (this.socket) {
      if (callback) {
        console.log('📡 Unsubscribing specific callback from event:', eventName);
        
        // Find and remove the wrapped callback
        if (this.listeners.has(eventName)) {
          const callbacks = this.listeners.get(eventName);
          const listenerObj = callbacks.find(obj => obj.original === callback);
          
          if (listenerObj) {
            this.socket.off(eventName, listenerObj.wrapped);
            const index = callbacks.indexOf(listenerObj);
            if (index > -1) {
              callbacks.splice(index, 1);
            }
          }
        }
      } else {
        console.log('📡 Unsubscribing all callbacks from event:', eventName);
        this.socket.off(eventName);
        this.listeners.delete(eventName);
      }
    }
  }

  /**
   * Emit an event to the server
   * @param {string} eventName - The event name to emit
   * @param {*} data - The data to send with the event
   */
  emit(eventName, data) {
    if (this.socket && this.isConnected) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📤 EMITTING EVENT TO SERVER');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('Event Name:', eventName);
      console.log('Timestamp:', new Date().toLocaleTimeString());
      console.log('Data Sent:', JSON.stringify(data, null, 2));
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      this.socket.emit(eventName, data);
    } else {
      console.warn('⚠️ Cannot emit event - socket not connected');
    }
  }

  /**
   * Listen for payment status updates
   * @param {string} orderId - The order ID to track
   * @param {function} callback - Callback function when payment status changes
   */
  onPaymentStatusUpdate(orderId, callback) {
    // Join the order-specific room
    this.joinRoom(`order_${orderId}`);
    
    // Listen for payment status updates
    this.on('payment_status_update', (data) => {
      console.log('💳 Payment status update received:', data);
      if (data.orderId === orderId || data.order_id === orderId) {
        callback(data);
      }
    });
  }

  /**
   * Listen for order updates
   * @param {string} orderId - The order ID to track
   * @param {function} callback - Callback function when order is updated
   */
  onOrderUpdate(orderId, callback) {
    // Join the order-specific room
    this.joinRoom(`order_${orderId}`);
    
    // Listen for order updates
    this.on('order_update', (data) => {
      console.log('📦 Order update received:', data);
      if (data.orderId === orderId || data.order_id === orderId) {
        callback(data);
      }
    });
  }

  /**
   * Listen for QR code payment confirmations
   * @param {string} orderId - The order ID to track
   * @param {function} callback - Callback function when QR payment is confirmed
   */
  onQRPaymentConfirmation(orderId, callback) {
    // Join the order-specific room
    this.joinRoom(`order_${orderId}`);
    
    // Listen for QR payment confirmations
    this.on('qr_payment_confirmed', (data) => {
      console.log('✅ QR Payment confirmed:', data);
      if (data.orderId === orderId || data.order_id === orderId) {
        callback(data);
      }
    });
  }

  /**
   * Check if socket is currently connected
   * @returns {boolean} Connection status
   */
  isSocketConnected() {
    return this.isConnected && this.socket && this.socket.connected;
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting from WebSocket server');
      
      // Remove all event listeners
      this.listeners.forEach((callbacks, eventName) => {
        this.socket.off(eventName);
      });
      this.listeners.clear();
      
      // Disconnect the socket
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  /**
   * Get the current socket instance
   * @returns {Socket|null} The socket instance or null
   */
  getSocket() {
    return this.socket;
  }

  /**
   * Get diagnostic information about the socket connection
   * @returns {object} Diagnostic information
   */
  getDiagnostics() {
    const diagnostics = {
      hasSocket: !!this.socket,
      isConnected: this.isConnected,
      socketConnected: this.socket ? this.socket.connected : false,
      socketId: this.socket ? this.socket.id : null,
      transport: this.socket?.io?.engine?.transport?.name || 'N/A',
      url: this.socket?.io?.uri || 'N/A',
      registeredListeners: Array.from(this.listeners.keys()),
      timestamp: new Date().toLocaleString()
    };

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔍 WEBSOCKET DIAGNOSTICS');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📊 Socket Status:', JSON.stringify(diagnostics, null, 2));
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    return diagnostics;
  }
}

// Export a singleton instance
const socketService = new SocketService();
export default socketService;